import { useQuery, useQueryClient } from '@tanstack/react-query';
import { keyBy } from 'lodash';
import { useContext, useEffect, useMemo, useRef } from 'react';

import { SocketContext } from '../providers/SocketProvider/SocketProvider';
import { ById, PartialParticipant } from '../services/backendService/types';
import { newRelicNoticeError } from './useNewRelic';

const STALE_TIME = 10 * 60 * 1000; // 10 minutes

// Merge changed participants into the existing list: update matching participants
// in place (merging fields, so a changed nickName is reflected and no field is lost)
// while preserving order, and append genuinely new joiners at the end.
export const mergeParticipants = (
  existing: PartialParticipant[],
  changed: PartialParticipant[],
): PartialParticipant[] => {
  const changedById = keyBy(changed, 'id');
  const existingIds = new Set(existing.map(p => p.id));

  const updatedExisting = existing.map(p =>
    changedById[p.id] ? { ...p, ...changedById[p.id] } : p,
  );
  const newlyJoined = changed.filter(p => !existingIds.has(p.id));

  return [...updatedExisting, ...newlyJoined];
};

export const useGameParticipants = (gameId: string, tenantId: string) => {
  // This hook is used to get the participants of a game and subscribe to changes
  // It is a singleton, meaning that it will only subscribe once and the subscription will be kept
  // so dont use this hook more than one time on the same page
  const { gameAPI } = useContext(SocketContext);
  const queryClient = useQueryClient();

  const participantsQueryKey = useMemo(() => ['game-participants', tenantId, gameId], [
    tenantId,
    gameId,
  ]);
  const countQueryKey = useMemo(() => ['game-participants-count', tenantId, gameId], [
    tenantId,
    gameId,
  ]);

  const participantsRef = useRef<PartialParticipant[]>([]);
  const intervalRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    if (!gameId || !gameAPI) return;

    const fetchParticipants = async ({ volatile }: { volatile: boolean }) => {
      const allParticipants = await gameAPI.game.listAllParticipants({
        gameId,
        tenantId,
        volatile,
      });
      if (!allParticipants) {
        queryClient.setQueryData<PartialParticipant[]>(participantsQueryKey, []);
        participantsRef.current = [];
        queryClient.setQueryData<number>(countQueryKey, 0);
        return;
      }
      queryClient.setQueryData<PartialParticipant[]>(
        participantsQueryKey,
        allParticipants,
      );
      queryClient.setQueryData<number>(countQueryKey, allParticipants.length);
      participantsRef.current = allParticipants;
    };

    fetchParticipants({ volatile: false });

    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    intervalRef.current = setInterval(
      () => fetchParticipants({ volatile: true }),
      1000 * 60,
    );

    const subChange = gameAPI.game
      .listChangedParticipants({ gameId, tenantId })
      .subscribe({
        next: rawParticipants => {
          // updates the participants list with the new participants and the existing one
          if (!rawParticipants || rawParticipants.length === 0) {
            return;
          }

          // merges the rawParticipants into the existing participants list,
          // updating changed participants in place and preserving order
          const newParticipants = mergeParticipants(
            participantsRef.current,
            rawParticipants,
          );
          queryClient.setQueryData<PartialParticipant[]>(
            participantsQueryKey,
            newParticipants,
          );
          participantsRef.current = newParticipants;
        },
        error: error => {
          newRelicNoticeError(
            error,
            'Error while listening to game participants changes',
          );
        },
      });

    const subCount = gameAPI.game.countParticipants({ gameId, tenantId }).subscribe({
      next: nParticipants => {
        // updates the participants list with the new participants and the existing one
        if (nParticipants !== undefined) {
          queryClient.setQueryData<number>(countQueryKey, nParticipants);
        }
      },
      error: error => {
        newRelicNoticeError(error, 'Error while listening to game participants count');
      },
    });

    return () => {
      subChange.unsubscribe();
      subCount.unsubscribe();
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [gameAPI, gameId, tenantId, queryClient, participantsQueryKey, countQueryKey]);

  const { data: participants } = useQuery<PartialParticipant[] | undefined>({
    queryKey: participantsQueryKey,
    queryFn: () => Promise.resolve(undefined),
    staleTime: STALE_TIME,
    enabled: Boolean(gameId && gameAPI),
  });

  const { data: numberOfParticipants } = useQuery<number>({
    queryKey: countQueryKey,
    queryFn: () => Promise.resolve(0),
    staleTime: STALE_TIME,
    enabled: Boolean(gameId && gameAPI),
  });

  const byId = useMemo(
    () =>
      (participants || []).reduce<ById<PartialParticipant>>(
        (acc, cur) => ({ ...acc, [cur.id]: cur }),
        {},
      ),
    [participants],
  );
  const participantsMemo = useMemo(() => (participants || []).filter(p => !p.isKicked), [
    participants,
  ]);

  // The realtime `countParticipants` stream reflects joins and leaves immediately, but the
  // backend keeps counting a participant after they are kicked. Kicks instead arrive in
  // realtime on the `listChangedParticipants` stream as an `isKicked: true` flag. Subtract
  // the locally-known kicked participants from the streamed count so the number drops the
  // instant a kick is broadcast, without waiting for the periodic full refetch.
  const count = useMemo(() => {
    const kickedCount = (participants || []).length - participantsMemo.length;
    return Math.max(0, (numberOfParticipants || 0) - kickedCount);
  }, [participants, participantsMemo, numberOfParticipants]);

  return {
    allParticipants: participants || [],
    participants: participantsMemo,
    count,
    byId,
  };
};
